import { lazy, Suspense, useEffect, useState } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LocaleProvider } from "@/contexts/LocaleContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { PlaneLoader } from "@/components/PlaneLoader";

// Lazy load toasters - they don't need to block initial render
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Eager load critical pages
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load other pages for better initial load performance
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const Product = lazy(() => import("./pages/Product"));
const Profile = lazy(() => import("./pages/Profile"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
// DynamicPage removed - orphaned import, no route uses it
const Guides = lazy(() => import("./pages/Guides"));
const Maps = lazy(() => import("./pages/Maps"));
const Planning = lazy(() => import("./pages/Planning"));
const BusinessContent = lazy(() => import("./pages/BusinessContent"));
const Services = lazy(() => import("./pages/Services"));
const HotelsDeals = lazy(() => import("./pages/HotelsDeals"));
const CarsDeals = lazy(() => import("./pages/CarsDeals"));
const FlightsDeals = lazy(() => import("./pages/FlightsDeals"));
const DealsPage = lazy(() => import("./pages/DealsPage"));
const EsimPage = lazy(() => import("./pages/EsimPage"));
const InsurancePage = lazy(() => import("./pages/InsurancePage"));
const AttractionsPage = lazy(() => import("./pages/AttractionsPage"));
const TicketsPage = lazy(() => import("./pages/TicketsPage"));
const DestinationsIsrael = lazy(() => import("./pages/DestinationsIsrael"));
const DestinationsWorld = lazy(() => import("./pages/DestinationsWorld"));
const RegionHubNorth = lazy(() => import("./pages/RegionHubNorth"));
const RegionHubCenter = lazy(() => import("./pages/RegionHubCenter"));
const RegionHubJerusalem = lazy(() => import("./pages/RegionHubJerusalem"));
const RegionHubSouth = lazy(() => import("./pages/RegionHubSouth"));
const RegionHubEilat = lazy(() => import("./pages/RegionHubEilat"));
const ContinentHubEurope = lazy(() => import("./pages/ContinentHubEurope"));
const ContinentHubAmerica = lazy(() => import("./pages/ContinentHubAmerica"));
const ContinentHubAsia = lazy(() => import("./pages/ContinentHubAsia"));
const ContinentHubAfrica = lazy(() => import("./pages/ContinentHubAfrica"));
const ContinentHubAustralia = lazy(() => import("./pages/ContinentHubAustralia"));
const PackingListTool = lazy(() => import("./pages/PackingListTool"));
const ToolsHub = lazy(() => import("./pages/ToolsHub"));
const CurrencyConverterTool = lazy(() => import("./pages/CurrencyConverterTool"));
const SocketCheckerTool = lazy(() => import("./pages/SocketCheckerTool"));
const SizeConverterTool = lazy(() => import("./pages/SizeConverterTool"));
const EmergencyNumbersTool = lazy(() => import("./pages/EmergencyNumbersTool"));
const TippingCalculatorTool = lazy(() => import("./pages/TippingCalculatorTool"));
const TransportAppTool = lazy(() => import("./pages/TransportAppTool"));
const DataUsageTool = lazy(() => import("./pages/DataUsageTool"));
const NatbagHubTool = lazy(() => import("./pages/NatbagHubTool"));
const WorldClockWeatherTool = lazy(() => import("./pages/WorldClockWeatherTool"));
const LanguageCardsTool = lazy(() => import("./pages/LanguageCardsTool"));
const InstagramCaptionTool = lazy(() => import("./pages/InstagramCaptionTool"));
const MoodPlaylistsTool = lazy(() => import("./pages/MoodPlaylistsTool"));
const TechPrepTool = lazy(() => import("./pages/TechPrepTool"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons"));
const AdminAffiliates = lazy(() => import("./pages/admin/AdminAffiliates"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminLogs = lazy(() => import("./pages/admin/AdminLogs"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminData = lazy(() => import("./pages/admin/AdminData"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminBanners = lazy(() => import("./pages/admin/AdminBanners"));
const AdminSeo = lazy(() => import("./pages/admin/AdminSeo"));
const GoRedirect = lazy(() => import("./pages/GoRedirect"));

// Protected route wrapper
import { ProtectedAdminRoute } from "./components/auth/ProtectedAdminRoute";

const queryClient = new QueryClient();

// Suspense fallback with plane loader
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <PlaneLoader />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LocaleProvider>
      <AuthProvider>
        <CartProvider>
          <TooltipProvider>
            {/* Lazy-loaded toasters - don't block initial paint */}
            <Suspense fallback={null}>
              <Toaster />
              <Sonner />
            </Suspense>
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/accessibility" element={<Accessibility />} />
                <Route path="/product/:id" element={<Product />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* Destination Hub Pages */}
              <Route path="/destinations" element={<Navigate to="/destinations/israel" replace />} />
              <Route path="/destinations/israel" element={<DestinationsIsrael />} />
              <Route path="/destinations/world" element={<DestinationsWorld />} />
              
              {/* Israel Destinations */}
              <Route path="/destinations/israel/north" element={<RegionHubNorth />} />
              <Route path="/destinations/israel/center" element={<RegionHubCenter />} />
              <Route path="/destinations/israel/south" element={<RegionHubSouth />} />
              <Route path="/destinations/israel/eilat" element={<RegionHubEilat />} />
              <Route path="/destinations/israel/jerusalem" element={<RegionHubJerusalem />} />
              
              {/* World Destinations */}
              <Route path="/destinations/world/europe" element={<ContinentHubEurope />} />
              <Route path="/destinations/world/america" element={<ContinentHubAmerica />} />
              <Route path="/destinations/world/asia" element={<ContinentHubAsia />} />
              <Route path="/destinations/world/africa" element={<ContinentHubAfrica />} />
              <Route path="/destinations/world/australia" element={<ContinentHubAustralia />} />
              
              {/* Services */}
              <Route path="/services" element={<Services />} />
              <Route path="/business-content" element={<BusinessContent />} />
              <Route path="/guides" element={<Guides />} />
              <Route path="/maps" element={<Maps />} />
              <Route path="/planning" element={<Planning />} />
              
              {/* Top bar services */}
              <Route path="/hotels" element={<HotelsDeals />} />
              <Route path="/cars" element={<CarsDeals />} />
              <Route path="/flights" element={<FlightsDeals />} />
              <Route path="/packages" element={<DealsPage />} />
              <Route path="/esim" element={<EsimPage />} />
              <Route path="/insurance" element={<InsurancePage />} />
              <Route path="/attractions" element={<AttractionsPage />} />
              <Route path="/tickets" element={<TicketsPage />} />
              
              {/* Tools */}
              <Route path="/tools" element={<ToolsHub />} />
              <Route path="/tools/packing-list" element={<PackingListTool />} />
              <Route path="/tools/currency-converter" element={<CurrencyConverterTool />} />
              <Route path="/tools/socket-checker" element={<SocketCheckerTool />} />
              <Route path="/tools/size-converter" element={<SizeConverterTool />} />
              <Route path="/tools/emergency-numbers" element={<EmergencyNumbersTool />} />
              <Route path="/tools/tipping-calculator" element={<TippingCalculatorTool />} />
              <Route path="/tools/transport-apps" element={<TransportAppTool />} />
              <Route path="/tools/data-usage" element={<DataUsageTool />} />
              <Route path="/tools/natbag-hub" element={<NatbagHubTool />} />
              <Route path="/tools/world-clock" element={<WorldClockWeatherTool />} />
              <Route path="/tools/language-cards" element={<LanguageCardsTool />} />
              <Route path="/tools/instagram-captions" element={<InstagramCaptionTool />} />
              <Route path="/tools/mood-playlists" element={<MoodPlaylistsTool />} />
              <Route path="/tools/tech-prep" element={<TechPrepTool />} />
              
              {/* Blog */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              
              {/* Cart & Checkout */}
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              
              {/* Admin Routes - Protected at route level */}
              <Route path="/admin" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
              <Route path="/admin/products" element={<ProtectedAdminRoute><AdminProducts /></ProtectedAdminRoute>} />
              <Route path="/admin/orders" element={<ProtectedAdminRoute><AdminOrders /></ProtectedAdminRoute>} />
              <Route path="/admin/coupons" element={<ProtectedAdminRoute><AdminCoupons /></ProtectedAdminRoute>} />
              <Route path="/admin/affiliates" element={<ProtectedAdminRoute><AdminAffiliates /></ProtectedAdminRoute>} />
              <Route path="/admin/users" element={<ProtectedAdminRoute><AdminUsers /></ProtectedAdminRoute>} />
              <Route path="/admin/blog" element={<ProtectedAdminRoute><AdminBlog /></ProtectedAdminRoute>} />
              <Route path="/admin/settings" element={<ProtectedAdminRoute><AdminSettings /></ProtectedAdminRoute>} />
              <Route path="/admin/data" element={<ProtectedAdminRoute><AdminData /></ProtectedAdminRoute>} />
              <Route path="/admin/media" element={<ProtectedAdminRoute><AdminMedia /></ProtectedAdminRoute>} />
              <Route path="/admin/banners" element={<ProtectedAdminRoute><AdminBanners /></ProtectedAdminRoute>} />
              <Route path="/admin/seo" element={<ProtectedAdminRoute><AdminSeo /></ProtectedAdminRoute>} />
              <Route path="/admin/logs" element={<ProtectedAdminRoute><AdminLogs /></ProtectedAdminRoute>} />
              
              {/* Affiliate Redirect */}
              <Route path="/go/:slug" element={<GoRedirect />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </CartProvider>
  </AuthProvider>
</LocaleProvider>
</QueryClientProvider>
);

export default App;
